import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { generateDymoLabelXml } from '../../utils/dymoLabelTemplate';
import { checkDymoConnectStatus, printLabel as dymoConnectPrint, DymoConnectPrinter } from '../../utils/dymoConnect';
import { checkZebraPrintStatus, printZpl, ZebraPrinter } from '../../utils/zebraPrint';
import { generateZebraLabelZpl, getTestLabelZpl, ZEBRA_CONFIG } from '../../utils/zebraLabelTemplate';

interface LabelData {
  orderId: string;
  customerId: string;
  partNumber: string;
  description: string;
  poNumber: string;
  date: string;
  itemId: number;
}

type PrinterType = 'zebra' | 'dymo' | 'browser';

const PrintLabel: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [labelData, setLabelData] = useState<LabelData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Printer states
  const [printerType, setPrinterType] = useState<PrinterType>('browser');
  const [zebraPrinters, setZebraPrinters] = useState<ZebraPrinter[]>([]);
  const [dymoPrinters, setDymoPrinters] = useState<DymoConnectPrinter[]>([]);
  const [selectedPrinter, setSelectedPrinter] = useState<string>('');
  const [zebraAvailable, setZebraAvailable] = useState<boolean>(false);
  const [dymoAvailable, setDymoAvailable] = useState<boolean>(false);
  const [printing, setPrinting] = useState<boolean>(false);
  const [copies, setCopies] = useState<number>(2);
  const [needsCertificate, setNeedsCertificate] = useState<boolean>(false);
  const [checkingPrinters, setCheckingPrinters] = useState<boolean>(false);

  // Check all printers function with useCallback
  const checkAllPrinters = useCallback(async () => {
    console.log('Starting printer check...');
    setCheckingPrinters(true);

    // Reset states first
    let foundZebra = false;
    let foundDymo = false;
    let certNeeded = false;

    // Check Zebra printers first (preferred)
    try {
      console.log('Checking Zebra Browser Print service...');
      const zebraStatus = await checkZebraPrintStatus();
      console.log('Zebra status:', zebraStatus);

      if (zebraStatus.isConnected && zebraStatus.printers.length > 0) {
        setZebraPrinters(zebraStatus.printers);
        setZebraAvailable(true);
        foundZebra = true;
        setPrinterType('zebra');
        setSelectedPrinter(zebraStatus.printers[0].name);
        setNeedsCertificate(false);
        console.log('Zebra Browser Print found! Printers:', zebraStatus.printers);
      } else {
        setZebraPrinters([]);
        setZebraAvailable(false);
        if (zebraStatus.needsCertificate) {
          certNeeded = true;
          setNeedsCertificate(true);
          console.log('Zebra Browser Print may need certificate acceptance');
        }
      }
    } catch (error) {
      console.log('Zebra not available:', error);
      setZebraPrinters([]);
      setZebraAvailable(false);
    }

    // Check DYMO printers as fallback
    try {
      console.log('Checking DYMO Connect service...');
      const dymoStatus = await checkDymoConnectStatus();
      console.log('DYMO status:', dymoStatus);

      if (dymoStatus.isConnected && dymoStatus.printers.length > 0) {
        setDymoPrinters(dymoStatus.printers);
        setDymoAvailable(true);
        foundDymo = true;
        // Only set as default if Zebra not available
        if (!foundZebra) {
          setPrinterType('dymo');
          setSelectedPrinter(dymoStatus.printers[0].name);
        }
        console.log('DYMO Connect found! Printers:', dymoStatus.printers);
      } else {
        setDymoPrinters([]);
        setDymoAvailable(false);
      }
    } catch (error) {
      console.log('DYMO not available:', error);
      setDymoPrinters([]);
      setDymoAvailable(false);
    }

    // If no printers found and no certificate issue, ensure needsCertificate is false
    if (!foundZebra && !foundDymo && !certNeeded) {
      setNeedsCertificate(false);
    }

    // Default to browser print if no label printers found
    if (!foundZebra && !foundDymo) {
      setPrinterType('browser');
      setSelectedPrinter('');
    }

    setCheckingPrinters(false);
    console.log('Printer check complete. Zebra:', foundZebra, 'DYMO:', foundDymo, 'CertNeeded:', certNeeded);
  }, []);

  useEffect(() => {
    // Get label data from localStorage (set by packaging page)
    const storedData = localStorage.getItem('labelData');
    if (storedData) {
      const data = JSON.parse(storedData);
      setLabelData(data);
      setLoading(false);
    } else {
      setLoading(false);
    }

    // Check for available printers
    checkAllPrinters();
  }, [checkAllPrinters]);

  const handleBrowserPrint = () => {
    window.print();

    // Auto-close after print
    setTimeout(() => {
      localStorage.removeItem('labelData');
      window.close();
    }, 1000);
  };

  const handleZebraPrint = async () => {
    if (!labelData || !selectedPrinter) {
      alert('Please select a printer');
      return;
    }

    setPrinting(true);

    try {
      // Generate ZPL for the label
      let zpl = '';
      for (let i = 0; i < copies; i++) {
        zpl += generateZebraLabelZpl({
          orderId: labelData.orderId,
          customerId: labelData.customerId,
          partNumber: labelData.partNumber,
          description: labelData.description,
          poNumber: labelData.poNumber,
          date: labelData.date,
          itemId: labelData.itemId,
        });
      }

      console.log('Printing to Zebra:', selectedPrinter);
      console.log('ZPL:', zpl);

      const result = await printZpl(selectedPrinter, zpl);
      if (!result.success) {
        alert('Failed to print label: ' + (result.error || 'Unknown error'));
        setPrinting(false);
        return;
      }

      console.log('Label sent to Zebra printer successfully!');
      setPrinting(false);

      // Auto-close after successful print
      setTimeout(() => {
        localStorage.removeItem('labelData');
        window.close();
      }, 1000);

    } catch (error) {
      console.error('Error printing with Zebra:', error);
      alert('Failed to print label. Error: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setPrinting(false);
    }
  };

  const handleDymoPrint = async () => {
    if (!labelData || !selectedPrinter) {
      alert('Please select a printer');
      return;
    }

    setPrinting(true);

    try {
      // Generate label XML
      const labelXml = generateDymoLabelXml({
        orderId: labelData.orderId,
        customerId: labelData.customerId,
        partNumber: labelData.partNumber,
        description: labelData.description,
        poNumber: labelData.poNumber,
        date: labelData.date
      });

      console.log('Printing to DYMO:', selectedPrinter);

      const result = await dymoConnectPrint(selectedPrinter, labelXml, copies);
      if (!result.success) {
        alert('Failed to print label: ' + (result.error || 'Unknown error'));
        setPrinting(false);
        return;
      }

      console.log('Label sent to DYMO printer successfully!');
      setPrinting(false);

      // Auto-close after successful print
      setTimeout(() => {
        localStorage.removeItem('labelData');
        window.close();
      }, 1000);

    } catch (error) {
      console.error('Error printing with DYMO:', error);
      alert('Failed to print label. Error: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setPrinting(false);
    }
  };

  const handlePrint = async () => {
    if (printerType === 'zebra') {
      await handleZebraPrint();
    } else if (printerType === 'dymo') {
      await handleDymoPrint();
    } else {
      handleBrowserPrint();
    }
  };

  const handleTestPrint = async () => {
    if (printerType !== 'zebra' || !selectedPrinter) {
      alert('Test print only available for Zebra printers');
      return;
    }

    setPrinting(true);
    try {
      const testZpl = getTestLabelZpl();
      const result = await printZpl(selectedPrinter, testZpl);
      if (!result.success) {
        alert('Test print failed: ' + (result.error || 'Unknown error'));
      } else {
        alert('Test label sent to printer!');
      }
    } catch (error) {
      alert('Test print error: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
    setPrinting(false);
  };

  const handleClose = () => {
    localStorage.removeItem('labelData');
    window.close();
  };

  const handlePrinterTypeChange = (type: PrinterType) => {
    setPrinterType(type);
    if (type === 'zebra' && zebraPrinters.length > 0) {
      setSelectedPrinter(zebraPrinters[0].name);
    } else if (type === 'dymo' && dymoPrinters.length > 0) {
      setSelectedPrinter(dymoPrinters[0].name);
    } else {
      setSelectedPrinter('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading label...</p>
        </div>
      </div>
    );
  }

  if (!labelData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 font-semibold">No label data found</p>
          <button
            onClick={handleClose}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Render label preview for Zebra 2x4" format
  const renderZebraLabel = () => (
    <div className="zebra-label-container">
      <div className="zebra-label">
        <div className="order-id">{labelData!.orderId}</div>
        <div className="customer-line">CUST: {labelData!.customerId}</div>

        <div className="divider"></div>

        <div className="part-label">PART#</div>
        <div className="part-number">{labelData!.partNumber}</div>

        <div className="divider"></div>

        <div className="desc-label">DESCRIPTION:</div>
        <div className="description">{labelData!.description}</div>

        <div className="divider"></div>

        <div className="po-line">PO#: {labelData!.poNumber}</div>
        <div className="date-line">DATE: {labelData!.date}</div>

        <div className="divider"></div>

        <div className="barcode-section">
          <div className="barcode-placeholder">
            ||||| {labelData!.orderId.replace(/-/g, '')} |||||
          </div>
          <div className="barcode-text">Scan for tracking</div>
        </div>

        <div className="footer">CalTrend Atlas</div>
      </div>
    </div>
  );

  // Render legacy DYMO label preview
  const renderDymoLabel = () => (
    <div className="label-container">
      <div className="label-header">
        <div className="left-section">
          <div className="order-id-dymo">{labelData!.orderId}</div>
          <div className="part-number-dymo">{labelData!.partNumber}</div>
        </div>
        <div className="center-section">
          <div className="customer-id-dymo">{labelData!.customerId}</div>
        </div>
        <div className="date-section-dymo">{labelData!.date}</div>
      </div>

      <div className="description-dymo">{labelData!.description}</div>

      <div className="label-footer">
        <div className="po-section-dymo">
          <span className="po-label-dymo">PO#:</span>
          <span className="po-number-dymo">{labelData!.poNumber}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Print styles for Zebra 2x4" labels */}
      <style>{`
        @media print {
          @page {
            size: 2in 4in;
            margin: 0;
          }
          .no-print {
            display: none !important;
          }
          body {
            margin: 0;
            padding: 0;
          }
          .zebra-label-container {
            border: none !important;
            box-shadow: none !important;
            page-break-inside: avoid;
          }
          .zebra-label {
            border: none !important;
          }
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
        }

        /* Zebra 2x4" Label Styles */
        .zebra-label-container {
          display: flex;
          justify-content: center;
          margin: 20px auto;
        }

        .zebra-label {
          width: 2in;
          height: 4in;
          background: white;
          border: 2px solid #333;
          padding: 8px;
          font-family: Arial, sans-serif;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .zebra-label .order-id {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 4px;
        }

        .zebra-label .customer-line {
          font-size: 12px;
          margin-bottom: 6px;
        }

        .zebra-label .divider {
          border-top: 1px solid #ccc;
          margin: 6px 0;
        }

        .zebra-label .part-label {
          font-size: 11px;
          font-weight: bold;
        }

        .zebra-label .part-number {
          font-size: 14px;
          font-weight: bold;
          margin-bottom: 4px;
        }

        .zebra-label .desc-label {
          font-size: 9px;
          color: #666;
        }

        .zebra-label .description {
          font-size: 10px;
          line-height: 1.3;
          min-height: 36px;
          word-wrap: break-word;
        }

        .zebra-label .po-line,
        .zebra-label .date-line {
          font-size: 11px;
          margin-bottom: 2px;
        }

        .zebra-label .barcode-section {
          text-align: center;
          margin: 10px 0;
        }

        .zebra-label .barcode-placeholder {
          font-family: 'Libre Barcode 128', monospace;
          font-size: 32px;
          letter-spacing: 2px;
          background: #f5f5f5;
          padding: 8px;
          border: 1px solid #ddd;
        }

        .zebra-label .barcode-text {
          font-size: 8px;
          color: #666;
          margin-top: 4px;
        }

        .zebra-label .footer {
          font-size: 8px;
          text-align: center;
          color: #999;
          margin-top: auto;
        }

        /* Legacy DYMO Label Styles */
        .label-container {
          width: 3.5in;
          height: 1.125in;
          background: white;
          border: 2px solid #333;
          border-radius: 8px;
          padding: 8px;
          margin: 0 auto 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .label-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 6px;
          padding-bottom: 3px;
          border-bottom: 1px solid #ccc;
        }

        .order-id-dymo {
          font-size: 11px;
          font-weight: bold;
        }

        .part-number-dymo {
          font-size: 9px;
          text-decoration: underline;
        }

        .customer-id-dymo {
          font-size: 9px;
          color: #999;
        }

        .date-section-dymo {
          font-size: 8px;
        }

        .description-dymo {
          font-size: 7px;
          line-height: 1.3;
          min-height: 24px;
        }

        .po-section-dymo {
          font-size: 8px;
        }

        .po-label-dymo {
          font-weight: bold;
          margin-right: 5px;
        }

        @media screen {
          body {
            background-color: #f0f0f0;
            padding: 20px;
          }
        }
      `}</style>

      {/* Screen-only controls */}
      <div className="no-print bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-blue-100 border border-blue-400 rounded p-4 mb-4">
            <p className="text-blue-800 font-semibold">Label Preview</p>
            <p className="text-blue-700 text-sm">Review the label below and click Print when ready</p>
          </div>

          {/* Printer Type Selection */}
          <div className="bg-white border border-gray-300 rounded p-4 mb-4">
            <p className="font-semibold mb-3">Select Printer Type:</p>
            <div className="flex flex-wrap gap-4 mb-4">
              {zebraAvailable && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="printerType"
                    checked={printerType === 'zebra'}
                    onChange={() => handlePrinterTypeChange('zebra')}
                    className="w-4 h-4"
                  />
                  <span className="font-medium text-green-700">Zebra ZD421 (2x4" labels)</span>
                </label>
              )}
              {dymoAvailable && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="printerType"
                    checked={printerType === 'dymo'}
                    onChange={() => handlePrinterTypeChange('dymo')}
                    className="w-4 h-4"
                  />
                  <span className="font-medium text-blue-700">DYMO LabelWriter</span>
                </label>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="printerType"
                  checked={printerType === 'browser'}
                  onChange={() => handlePrinterTypeChange('browser')}
                  className="w-4 h-4"
                />
                <span className="font-medium text-gray-700">Browser Print</span>
              </label>
            </div>

            {/* Printer Selection */}
            {printerType === 'zebra' && zebraPrinters.length > 0 && (
              <div className="flex items-center gap-4 mb-4">
                <label className="font-medium">Zebra Printer:</label>
                <select
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded bg-white"
                >
                  {zebraPrinters.map((printer) => (
                    <option key={printer.uid || printer.name} value={printer.name}>
                      {printer.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleTestPrint}
                  disabled={printing}
                  className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm disabled:opacity-50"
                >
                  Test Print
                </button>
              </div>
            )}

            {printerType === 'dymo' && dymoPrinters.length > 0 && (
              <div className="flex items-center gap-4 mb-4">
                <label className="font-medium">DYMO Printer:</label>
                <select
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded bg-white"
                >
                  {dymoPrinters.map((printer) => (
                    <option key={printer.name} value={printer.name}>
                      {printer.name} {printer.isConnected ? '(Connected)' : '(Offline)'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Copies */}
            {(printerType === 'zebra' || printerType === 'dymo') && (
              <div className="flex items-center gap-4">
                <label className="font-medium">Copies:</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                  className="w-20 px-3 py-2 border border-gray-300 rounded"
                />
              </div>
            )}
          </div>

          {/* Status Messages */}
          {zebraAvailable && printerType === 'zebra' && (
            <div className="bg-green-100 border border-green-400 rounded p-4 mb-4">
              <p className="text-green-800 font-semibold">Zebra ZD421 Ready</p>
              <p className="text-green-700 text-sm">Label size: {ZEBRA_CONFIG.labelWidth}" x {ZEBRA_CONFIG.labelHeight}" ({ZEBRA_CONFIG.dpi} DPI)</p>
            </div>
          )}

          {!zebraAvailable && !dymoAvailable && needsCertificate && (
            <div className="bg-orange-100 border border-orange-400 rounded p-4 mb-4">
              <p className="text-orange-800 font-semibold">Zebra Browser Print - Security Certificate Required</p>
              <p className="text-orange-700 text-sm mb-2">
                To securely connect to your Zebra printer, you need to accept the HTTPS certificate:
              </p>
              <ol className="text-orange-700 text-sm list-decimal list-inside space-y-1 mb-3">
                <li>Make sure <strong>Zebra Browser Print</strong> is installed and running on this PC</li>
                <li>Click "Accept Certificate" button below (opens new tab)</li>
                <li>In the new tab, click "Advanced" then "Proceed to localhost (unsafe)"</li>
                <li>Close that tab and click "Retry Connection" here</li>
              </ol>
              <div className="flex gap-2">
                <a
                  href="https://localhost:9101/available"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm font-medium inline-block"
                >
                  1. Accept Certificate
                </a>
                <button
                  onClick={() => checkAllPrinters()}
                  disabled={checkingPrinters}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-wait"
                >
                  {checkingPrinters ? 'Checking...' : '2. Retry Connection'}
                </button>
              </div>
            </div>
          )}

          {!zebraAvailable && !dymoAvailable && !needsCertificate && (
            <div className="bg-yellow-100 border border-yellow-400 rounded p-4 mb-4">
              <p className="text-yellow-800 font-semibold">No Label Printers Detected</p>
              <p className="text-yellow-700 text-sm mb-2">
                For direct printing, install one of the following on this PC:
              </p>
              <ul className="text-yellow-700 text-sm list-disc list-inside space-y-1">
                <li><strong>Zebra Browser Print</strong> - For Zebra ZD421 (recommended) - <a href="https://www.zebra.com/us/en/support-downloads/software/printer-software/browser-print.html" target="_blank" rel="noopener noreferrer" className="underline">Download</a></li>
                <li><strong>DYMO Connect</strong> - For DYMO LabelWriter</li>
              </ul>
              <p className="text-yellow-600 text-xs mt-2">
                The printer must be connected to this computer via USB.
              </p>
              <button
                onClick={() => checkAllPrinters()}
                disabled={checkingPrinters}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-wait"
              >
                {checkingPrinters ? 'Checking...' : 'Retry Connection'}
              </button>
            </div>
          )}

          {checkingPrinters && (
            <div className="bg-blue-100 border border-blue-400 rounded p-4 mb-4">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-blue-800 font-semibold">Checking for printers...</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="text-center space-x-2">
            <button
              onClick={handlePrint}
              disabled={printing}
              className="px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {printing ? 'Printing...' : `Print ${copies > 1 ? `(${copies} copies)` : ''}`}
            </button>
            <button
              onClick={handleClose}
              disabled={printing}
              className="px-6 py-3 bg-red-600 text-white rounded hover:bg-red-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Label Preview */}
      <div className="flex items-center justify-center min-h-screen">
        {printerType === 'zebra' || printerType === 'browser' ? (
          renderZebraLabel()
        ) : (
          <div className="flex gap-5">
            {renderDymoLabel()}
            {renderDymoLabel()}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintLabel;
